import React, { Component } from "react";
import {
    Box,
    FormControl,
    FormGroup,
    FormHelperText,
    FormLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import AlertDialogPopop from "../../Component/AlertDialogPopop/AlertDialogPopop";
import InputTextField from "../../Component/Reusables/InputTextField";
import { ShowAlert } from "../../Component/ShowAlert";
import { amountFormat } from '../../Component/helper';
import { loadFromLocalStorage } from "../../Common/Validation/LocalStorage";
import { SyncContext, SyncContextType } from "../../Contexts/BatchSyncContext";
import { CreateRulesProps, CreateRulesState, RulePair } from "./Interface";
import ReferralService from "../../Services/ReferralService";

class CreateRules extends Component<CreateRulesProps, CreateRulesState> {
    rulePairItemRefs: React.RefObject<HTMLDivElement>[] = [];
    static contextType = SyncContext;
    context!: SyncContextType;

    constructor(props: CreateRulesProps) {
        super(props);
        this.state = {
            accessToken: "",
            ruleName: '',
            ruleDescription: '',
            selectedCorridor: '',
            selectedCorridorCountryId: '',
            selectedRule: null,
            rulePairs: [],
            errors: {
                ruleName: '',
                ruleDescription: '',
                selectedCorridor: '',
                rulePairs: [],
            },
            isSaveConfirmationDialogOpen: false,
            corridorList: [],
            defaultCurrencySymbol: "$",
        } as CreateRulesState;
    }

    async componentDidMount() {
        try {
            const accessToken_ = await loadFromLocalStorage('accessToken');
            const profile_ = await loadFromLocalStorage('profile');

            if (!accessToken_ || !profile_) {
                localStorage.clear();
                window.location.href = '/';
                return;
            }

            const accessToken = accessToken_.toString();
            this.setState({ accessToken }, async () => {
                this.getAvilableCorridorList();
            });
        } catch (error) {
            console.error("Error in componentDidMount:", error);
            localStorage.clear();
            window.location.href = '/';
        }
    }

    // Fetch available corridors list
    private async getAvilableCorridorList() {
        try {
            //Call API to fetch corridor list
            const response = await ReferralService.getCorridorList(this.state.accessToken);

            const data = await response.json();

            if (!data.length) return ShowAlert("Information", "No corridors available. You can update the bonus and other settings in the existing rules under the Bonus Credits tab.", "info");

            this.setState({ corridorList: data || [] });
        } catch (error) {
            ShowAlert("Error", "Failed to load corridor list. Please try again later.", "error");
            this.setState({ corridorList: [] });
        }
    }

    // Handler to update the text input fields "ruleName" & "ruleDescription"
    handleTextChange = (field: 'ruleName' | 'ruleDescription') => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const maxLengths = {
            ruleName: 40,
            ruleDescription: 100,
        };

        const rawValue = event.target.value;
        const maxLength = maxLengths[field];
        let value = maxLength ? rawValue.slice(0, maxLength) : rawValue;

        // Capitalize first letter
        value = value.charAt(0).toUpperCase() + value.slice(1);

        this.setState(prevState => ({
            ...prevState,
            [field]: value,
            errors: {
                ...prevState.errors,
                [field]: "" // Clear error when typing
            }
        }));
    };

    //Get rules by selected corridor with curreny code
    getRulesByCorridor = async (event: any) => {
        const selectedValue = event.target.value as string;

        try {
            let currencyCode = "";
            let countryId = "";

            if (selectedValue) {
                const selected = JSON.parse(selectedValue);
                currencyCode = selected.currencyCode
                countryId = selected.countryId

                // Clear corridor error immediately when selecting
                this.setState(prevState => ({
                    selectedCorridor: selectedValue,
                    selectedCorridorCountryId: countryId,
                    errors: {
                        ...prevState.errors,
                        selectedCorridor: "" // Clear the error
                    }
                }));
            }
            
            //Call your API service to get rules for the selected corridor
            const response = await ReferralService.getRulesByCorridorCurrency(this.state.accessToken, currencyCode, countryId);

            const data = await response.json();

            this.setState({
                rulePairs: data || []
            });
        } catch (error) {
            ShowAlert("Error", "Failed to load rules for the selected corridor. Please try again later.", "error"
            );
            this.setState({ rulePairs: [], selectedCorridor: "" });
        }
    };

    // Field validation before submitting the form
    validateFields = (): boolean => {
        const { ruleName, ruleDescription, selectedCorridor, rulePairs } = this.state;

        const errors = {
            ruleName: ruleName.trim() ? "" : "Rule name is required.",
            ruleDescription: ruleDescription.trim() ? "" : "Rule description is required.",
            selectedCorridor: selectedCorridor ? "" : "Please select a corridor first",
            rulePairs: rulePairs.map(() => ({
                referrerCurrency: "",
                referrerBonusCredit: "",
                refereeCurrency: "",
                refereeBonusCredit: "",
                refereeMinTransaction: "",
            }))
        };

        // Only validate rule pairs if corridor is selected
        if (selectedCorridor) {
            rulePairs.forEach((rulePairItem, index) => {
                if (!rulePairItem.corridorCurrency) {
                    errors.rulePairs[index].referrerCurrency = "Corridor currency is required";
                }
                if (!rulePairItem.refereeCurrency) {
                    errors.rulePairs[index].refereeCurrency = "Referee currency is required";
                }
                if (
                    !rulePairItem.referrerBonusCredit ||
                    parseFloat(rulePairItem.referrerBonusCredit) <= 0 ||
                    parseFloat(rulePairItem.referrerBonusCredit) > parseFloat(rulePairItem.refereeMinTransaction)
                ) {
                    errors.rulePairs[index].referrerBonusCredit = "Bonus must be greater than 0 and less than or equal to Min Transaction amount";
                }
                if (
                    !rulePairItem.refereeBonusCredit ||
                    parseFloat(rulePairItem.refereeBonusCredit) <= 0 ||
                    parseFloat(rulePairItem.refereeBonusCredit) > parseFloat(rulePairItem.refereeMinTransaction)
                ) {
                    errors.rulePairs[index].refereeBonusCredit = "Bonus must be greater than 0 and less than or equal to Min Transaction amount";
                }

                if (!rulePairItem.refereeMinTransaction || parseFloat(rulePairItem.refereeMinTransaction) <= 0) {
                    errors.rulePairs[index].refereeMinTransaction = "Value must be greater than 0";
                }
            });
        }

        this.setState({ errors }, () => {
            // After state is updated, scroll to the first error
            this.scrollToFirstError();
        });

        const noRuleErrors = !errors.ruleName && !errors.ruleDescription;
        const noCorridorError = !errors.selectedCorridor;
        const noPairErrors = selectedCorridor ?
            errors.rulePairs.every(pair => Object.values(pair).every(error => !error)) :
            true;

        return noRuleErrors && noCorridorError && noPairErrors;
    };

    //Scroll into view when save
    scrollToFirstError = () => {
        const { errors } = this.state;

        // Check for top-level errors first
        if (errors.ruleName) {
            document.getElementById('rule-name')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (errors.ruleDescription) {
            document.getElementById('rule-description')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        if (errors.selectedCorridor) {
            // Assuming the corridor select has an id 'corridor-select'
            document.getElementById('corridor-select')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        // Check for rule pair errors
        if (errors.rulePairs) {
            for (let i = 0; i < errors.rulePairs.length; i++) {
                const pairErrors = errors.rulePairs[i];
                if (Object.values(pairErrors).some(error => error)) {
                    // Scroll to the rule pair with error
                    this.rulePairItemRefs[i]?.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                    return;
                }
            }
        }
    };

    //Handle save, this makes api fetch to post with created rule
    handleSave = async () => {
        if (!this.validateFields()) {
            const { errors } = this.state;
            if (errors.selectedCorridor) {
                ShowAlert("Validation Error", "Please select a corridor before proceeding.", "error");
            }
            return;
        }

        const { ruleName, ruleDescription, rulePairs } = this.state;

        try {
            const formattedEntries = rulePairs.map(rulePairItem => ({
                ...rulePairItem,
                referrerBonusCredit: parseFloat(rulePairItem.referrerBonusCredit.replace(/,/g, '')) || 0,
                refereeBonusCredit: parseFloat(rulePairItem.refereeBonusCredit.replace(/,/g, '')) || 0,
                refereeMinTransaction: parseFloat(rulePairItem.refereeMinTransaction.replace(/,/g, '')) || 0
            }));

            const postData = {
                ruleName: ruleName,
                ruleDescription: ruleDescription,
                pairs: formattedEntries
            }

            //Call API services to create a new rules
            const response = await ReferralService.createRules(this.state.accessToken, postData);

            if (response.ok) {
                ShowAlert("Success", "Rules created successfully.", "success")
                    .then(() => {
                        if (this.props.goToTab) {
                            this.props.goToTab(0);
                        }
                    });
            }
        } catch (error) {
            console.error("Error saving rules:", error);
            ShowAlert("Error", "An unexpected error occurred while saving rules.", "error");
        }
    };

    handleBonusAndMinTrans = (
        field: 'referrerBonusCredit' | 'refereeBonusCredit' | 'refereeMinTransaction',
        index: number,
        value: string
    ) => {
        let sanitizedValue = value.replace(/[^0-9.]/g, "");
        const MAX_LIMIT = 999999999;

        const decimalCount = (sanitizedValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
            sanitizedValue = sanitizedValue.slice(0, sanitizedValue.lastIndexOf(".") + 1);
        }

        if (parseFloat(sanitizedValue) > MAX_LIMIT) {
            sanitizedValue = MAX_LIMIT.toString();
        }

        this.setState(prevState => {
            const updatedPairs = [...prevState.rulePairs];
            updatedPairs[index] = {
                ...updatedPairs[index],
                [field]: sanitizedValue
            };

            const updatedErrors = { ...prevState.errors };
            if (!updatedErrors.rulePairs) {
                updatedErrors.rulePairs = [];
            }
            if (updatedErrors.rulePairs[index]) {
                updatedErrors.rulePairs[index][field] = "";
            }

            return {
                rulePairs: updatedPairs,
                errors: updatedErrors,
            };
        });
    };

    handleBonusAndMinTransBlur = (
        field: 'referrerBonusCredit' | 'refereeBonusCredit' | 'refereeMinTransaction',
        index: number
    ) => {
        this.setState(prevState => {
            const updatedPairs = [...prevState.rulePairs];
            const value = updatedPairs[index][field];
            const raw = value ? value.replace(/,/g, '') : '0';

            const updatedAmount = amountFormat(
                parseFloat(raw),
                "en-US",
                "USD"
            ).replace("$", "");

            updatedPairs[index] = {
                ...updatedPairs[index],
                [field]: updatedAmount
            };

            return { rulePairs: updatedPairs };
        });
    };

    handleBonusAndMinTransFocus = (
        field: 'referrerBonusCredit' | 'refereeBonusCredit' | 'refereeMinTransaction',
        index: number
    ) => {
        this.setState(prevState => {
            const updatedPairs = [...prevState.rulePairs];
            const value = updatedPairs[index][field];
            const numeric = value.replace(/,/g, "");
            updatedPairs[index] = {
                ...updatedPairs[index],
                [field]: numeric
            };
            return { rulePairs: updatedPairs };
        });
    };

    render() {
        const {
            corridorList,
            errors,
            isSaveConfirmationDialogOpen,
            ruleDescription,
            ruleName,
            rulePairs,
            selectedCorridor,
        } = this.state;
        return (
            <Box>
                <FormGroup>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
                        <fieldset className="fieldset-border">
                            <FormLabel component="legend" sx={{ fontWeight: 'bold' }}>Create new rules</FormLabel>
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                <Box style={{ display: 'flex', gap: '20px' }}>
                                    <Box style={{ flex: 1 }}>
                                        <TextField
                                            id="rule-name"
                                            label="Rule Name"
                                            variant="standard"
                                            fullWidth
                                            value={ruleName}
                                            onChange={this.handleTextChange('ruleName')}
                                            error={Boolean(errors.ruleName)}
                                            helperText={errors.ruleName || ""}
                                            inputProps={{
                                                maxLength: 40
                                            }}
                                        />
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        <TextField
                                            id="rule-description"
                                            label="Rule Description"
                                            variant="standard"
                                            fullWidth
                                            value={ruleDescription}
                                            onChange={this.handleTextChange('ruleDescription')}
                                            error={Boolean(errors.ruleDescription)}
                                            helperText={errors.ruleDescription || ""}
                                            inputProps={{
                                                maxLength: 100
                                            }}
                                        />
                                    </Box>
                                    <Box style={{ flex: 1 }}></Box>
                                </Box>
                                <Box style={{ display: 'flex', gap: '20px' }}>
                                    <Box style={{ flex: 1 }}>
                                        <FormGroup>
                                            <FormLabel className="input-field-label">Corridor</FormLabel>
                                            <FormControl error={!!errors.selectedCorridor} fullWidth>
                                                <Select
                                                    id="corridor-select"
                                                    color="secondary"
                                                    variant="outlined"
                                                    value={selectedCorridor}
                                                    onChange={this.getRulesByCorridor}
                                                    fullWidth
                                                    displayEmpty
                                                >
                                                    <MenuItem value="" disabled>
                                                        <>Select corridor</>
                                                    </MenuItem>
                                                    {corridorList.map((rule, index) => (
                                                        <MenuItem key={index}
                                                            value={JSON.stringify({
                                                                currencyCode: rule.currencyCode,
                                                                countryId: rule.countryId
                                                            })}
                                                            >
                                                            {rule.countryName} ({rule.currencyCode})
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                                {errors.selectedCorridor && (
                                                    <FormHelperText error>{errors.selectedCorridor}</FormHelperText>
                                                )}
                                            </FormControl>
                                        </FormGroup>
                                    </Box>
                                    <Box style={{ flex: 1 }}></Box>
                                    <Box style={{ flex: 1 }}></Box>
                                </Box>
                                <Box>
                                    {rulePairs.map((rulePairItem, index) => this.renderReferralrulePairItem(rulePairItem, index))}
                                </Box>
                            </Box>
                        </fieldset>
                    </Box>
                </FormGroup >
                <AlertDialogPopop
                    open={isSaveConfirmationDialogOpen}
                    alertTitle='Confirmation'
                    alertBody={`Are you sure you want to save the settings?`}
                    onClose={(state_, action_) => {
                        if (action_ === "ok") {
                            this.handleSave();
                        }
                        this.setState({
                            isSaveConfirmationDialogOpen: false,
                        });
                    }}
                />
            </Box >
        );
    }

    // This is the function that gets called when the user selects the corridor
    // Based on the avilable pairs its populates the rule pairs
    renderReferralrulePairItem = (rulePairItem: RulePair, index: number) => {
        const { errors, defaultCurrencySymbol } = this.state;

        const rowStyle = {
            display: 'flex',
            gap: '10px',
            alignItems: 'flex-start',
            marginBottom: 10,
            marginTop: 2
        };

        const inputBoxStyle = { flex: 1 };

        if (!this.rulePairItemRefs[index]) {
            this.rulePairItemRefs[index] = React.createRef();
        }

        return (
            <Box
                key={index}
                ref={this.rulePairItemRefs[index]}
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingBottom: 20,
                }}
            >
                <fieldset className="fieldset-border" style={{ flex: 1 }}>
                    <FormLabel component="legend" sx={{ fontWeight: 600 }}>
                        {`Pair ${index + 1}, ${rulePairItem.refereeCountryName && rulePairItem.corridorCountryName
                            ? `${rulePairItem.refereeCountryName} to ${rulePairItem.corridorCountryName}`
                            : ''
                            }`}
                    </FormLabel>
                    <Box style={rowStyle}>
                        {/*Referee Country*/}
                        <Box style={inputBoxStyle}>
                            <FormGroup>
                                <FormLabel className='input-field-label'>Referee country</FormLabel>
                                <Typography variant="body1" sx={{
                                    padding: '16px 0',
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                                    fontWeight: "bold"
                                }}>
                                    {rulePairItem.refereeCountryName || "-"} ({rulePairItem.refereeCurrency || "-"})
                                </Typography>
                                {errors.rulePairs && errors.rulePairs[index]?.refereeCurrency && (
                                    <FormHelperText error>
                                        {errors.rulePairs[index]?.refereeCurrency}
                                    </FormHelperText>
                                )}
                            </FormGroup>
                        </Box>
                        {/* Corridor Country */}
                        <Box style={inputBoxStyle}>
                            <FormGroup>
                                <FormLabel className='input-field-label'>Corridor country</FormLabel>
                                <Typography variant="body1" sx={{
                                    padding: '16px 0',
                                    borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
                                    fontWeight: "bold"
                                }}>
                                    {rulePairItem.corridorCountryName || "-"} ({rulePairItem.corridorCurrency || "-"})
                                </Typography>
                            </FormGroup>
                        </Box>
                        {/* Minimum transaction */}
                        <Box style={inputBoxStyle}>
                            <FormGroup>
                                <InputTextField
                                    label="Referee Min. transaction"
                                    value={rulePairItem.refereeMinTransaction}
                                    startAdornment={rulePairItem.refereeCurrencySymbol ?? "$"}
                                    inputType="number"
                                    onChange={(value) => this.handleBonusAndMinTrans('refereeMinTransaction', index, value)}
                                    onBlur={() => this.handleBonusAndMinTransBlur('refereeMinTransaction', index)}
                                    onFocus={() => this.handleBonusAndMinTransFocus('refereeMinTransaction', index)}
                                    error={Boolean(errors.rulePairs && errors.rulePairs[index]?.refereeMinTransaction)}
                                    helperText={errors.rulePairs?.[index]?.refereeMinTransaction || ""}
                                />
                            </FormGroup>
                        </Box>
                        {/* Referee bonus credit */}
                        <Box style={inputBoxStyle}>
                            <FormGroup>
                                <InputTextField
                                    label="Referee bonus credit"
                                    value={rulePairItem.refereeBonusCredit}
                                    startAdornment={rulePairItem.refereeCurrencySymbol ?? "$"}
                                    inputType="number"
                                    onChange={(value) => this.handleBonusAndMinTrans('refereeBonusCredit', index, value)}
                                    onBlur={() => this.handleBonusAndMinTransBlur('refereeBonusCredit', index)}
                                    onFocus={() => this.handleBonusAndMinTransFocus('refereeBonusCredit', index)}
                                    error={Boolean(errors.rulePairs?.[index]?.refereeBonusCredit)}
                                    helperText={errors.rulePairs?.[index]?.refereeBonusCredit || ""}
                                />
                            </FormGroup>
                        </Box>
                        {/* Referrer bonus credit */}
                        <Box style={inputBoxStyle}>
                            <FormGroup>
                                <InputTextField
                                    label="Referrer bonus credit"
                                    value={rulePairItem.referrerBonusCredit}
                                    startAdornment={rulePairItem.refereeCurrencySymbol ?? "$"}
                                    inputType="number"
                                    onChange={(value) => this.handleBonusAndMinTrans('referrerBonusCredit', index, value)}
                                    onBlur={() => this.handleBonusAndMinTransBlur('referrerBonusCredit', index)}
                                    onFocus={() => this.handleBonusAndMinTransFocus('referrerBonusCredit', index)}
                                    error={Boolean(errors.rulePairs?.[index]?.referrerBonusCredit)}
                                    helperText={errors.rulePairs?.[index]?.referrerBonusCredit || ""}
                                />
                            </FormGroup>
                        </Box>
                    </Box>
                </fieldset>
            </Box>
        );
    };
}

export default CreateRules;